import { useCallback, useEffect, useRef, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type ArquivoInfo } from "../api";
import { IconClose, IconAward, IconUpload, IconDownload, IconTrash, IconFile } from "../icons";
import { usePodeExcluir } from "../permissoes";

interface Props {
  treinamento: Row; // linha de treinamento (tem id, colaborador_id, nome_treinamento, colaborador_nome)
  onClose: () => void;
  onChanged: () => void; // recarrega listas
}

function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Certificado do colaborador para um treinamento. O arquivo é guardado na pasta
 * do colaborador com o prefixo "Cert-T<id> - " (aparece também na pasta dele).
 */
export default function CertificadoTreinoModal({ treinamento, onClose, onChanged }: Props) {
  const treinoId = treinamento.id as number;
  const colaboradorId = treinamento.colaborador_id as number | null;
  const prefixo = `Cert-T${treinoId} - `;
  const podeExcluir = usePodeExcluir(); // certificado já anexado só sai por admin

  const [arquivos, setArquivos] = useState<ArquivoInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const carregar = useCallback(async () => {
    if (!colaboradorId) {
      setLoading(false);
      setError("Este treinamento não tem colaborador vinculado.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const todos = await api.arquivos.list(colaboradorId);
      setArquivos(todos.filter((a) => a.nome.startsWith(prefixo)));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao listar certificados");
    } finally {
      setLoading(false);
    }
  }, [colaboradorId, prefixo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  async function enviar(files: FileList | null) {
    if (!files || files.length === 0) return;
    setEnviando(true);
    setError(null);
    try {
      await api.treinamentos.uploadCertificado(treinoId, files);
      if (fileRef.current) fileRef.current.value = "";
      await carregar();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao enviar o certificado");
    } finally {
      setEnviando(false);
    }
  }

  async function remover(nome: string) {
    if (!colaboradorId) return;
    if (!confirm(`Excluir "${nome.slice(prefixo.length)}"?`)) return;
    try {
      await api.arquivos.remove(colaboradorId, nome);
      await carregar();
      onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ia" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconAward size={18} /> Certificado do colaborador
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="concluir-resumo">
            <strong>{treinamento.nome_treinamento}</strong>
            <span>{treinamento.colaborador_nome || "Colaborador"}</span>
          </div>

          <div className="pasta-upload">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt,.zip"
              hidden
              onChange={(e) => enviar(e.target.files)}
            />
            <button
              type="button"
              className="btn-primary"
              onClick={() => fileRef.current?.click()}
              disabled={enviando || !colaboradorId}
            >
              <IconUpload size={16} /> {enviando ? "Enviando…" : "Anexar certificado"}
            </button>
            <span className="hint">Certificado do colaborador (PDF/imagem). Fica também na pasta dele.</span>
          </div>

          {error && <div className="form-error">{error}</div>}

          {loading ? (
            <div className="empty">Carregando…</div>
          ) : arquivos.length === 0 ? (
            <div className="empty">Nenhum certificado anexado a este treinamento ainda.</div>
          ) : (
            <ul className="pasta-list">
              {arquivos.map((a) => (
                <li key={a.nome} className="pasta-item">
                  <IconFile size={18} />
                  <div className="pasta-info">
                    <a
                      href={a.url ?? api.arquivos.url(colaboradorId as number, a.nome)}
                      target="_blank"
                      rel="noreferrer"
                      className="pasta-nome"
                    >
                      {a.nome.slice(prefixo.length)}
                    </a>
                    <span className="pasta-meta">
                      {tamanhoLegivel(a.tamanho)} • {new Date(a.enviado_em).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <a
                    className="icon-btn"
                    title="Baixar"
                    href={a.url ?? api.arquivos.url(colaboradorId as number, a.nome)}
                    download={a.nome.slice(prefixo.length)}
                  >
                    <IconDownload size={17} />
                  </a>
                  {podeExcluir && (
                    <button
                      className="icon-btn icon-btn-danger"
                      title="Excluir"
                      onClick={() => remover(a.nome)}
                    >
                      <IconTrash size={17} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
